// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { useEvolvePost } from '@evolvemep/api-ts';
import { ActionIcon, Button, Flex, Pill, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { emailRegEx } from 'constants/regEx';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { useUser } from 'hooks/user/useUser';

type Props = {
  refresh: () => void;
};

export const InviteUsersModal = ({ refresh }: Props) => {
  const {
    user: { companyId },
  } = useUser();
  const hasModifyCompanyMembersAccess = useHasAccess('companyMembers', 'write');
  const [opened, setOpened] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [emailPlaceholder, setEmailPlaceholder] = useState('');

  const { apiCall: addUserToCompany } = useEvolvePost('admin/user');

  if (!hasModifyCompanyMembersAccess) return null;

  const open = () => {
    setEmails([]);
    setError('');
    setOpened(true);
  };

  const onSubmit = async () => {
    setError('');
    await Promise.all(
      emails.map((userEmail) =>
        addUserToCompany({
          companyId,
          userEmail,
        }),
      ),
    ).then(() => {
      notifications.show({
        title: 'Successfully invited',
        message: `Member${emails.length === 1 ? '' : 's'} invited`,
        color: 'green',
      });
      refresh();
    });
  };

  const onAddCustomEmail = () => {
    const email = emailPlaceholder.trim();
    if (!email) return;

    if (emailRegEx.test(email)) {
      const existingEmail = emails.find((e) => e.toLowerCase() === email.toLowerCase());
      if (existingEmail) {
        setError(`Email already added: ${existingEmail}`);
      } else {
        setEmails([...new Set([...emails, email])]);
        setEmailPlaceholder('');
      }
    } else {
      setError(`Invalid email: ${email}`);
    }
  };

  return (
    <>
      <Button onClick={open} leftSection={<EvolveIcon icon="Add" size="sm" />} variant="light">
        Invite members
      </Button>
      <WrappedModal
        title="Invite members"
        opened={opened}
        onClose={() => setOpened(false)}
        onConfirm={onSubmit}
        confirmationText="Invite"
        disabled={emails.length === 0}
      >
        <Flex gap="sm">
          <TextInput
            placeholder="Enter email..."
            autoFocus
            data-autofocus
            value={emailPlaceholder}
            onChange={(e) => {
              setError('');
              setEmailPlaceholder(e.target.value);
            }}
            maxLength={64}
            onBlur={onAddCustomEmail}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onAddCustomEmail();
                e.stopPropagation();
                e.preventDefault();
              }
            }}
            style={{ width: '100%' }}
            error={error}
          />
          <AgGridStyleTooltip label="You can also add emails by hitting enter." openDelay={0} withArrow>
            <ActionIcon size="input-sm" variant="light" disabled={!emailPlaceholder} onClick={onAddCustomEmail}>
              <EvolveIcon icon="Add" size="sm" />
            </ActionIcon>
          </AgGridStyleTooltip>
        </Flex>

        <Flex gap="xs" wrap="wrap" mt="sm">
          {emails.map((email, i) => (
            <Pill key={email} withRemoveButton onRemove={() => setEmails((e) => [...e.slice(0, i), ...e.slice(i + 1)])}>
              {email}
            </Pill>
          ))}
        </Flex>
      </WrappedModal>
    </>
  );
};
